// =============================================================================
// AramisLogin.js — Écran de connexion / inscription Aramis (mot de passe +
// TOTP + passkey + deep-link ?signup=1), extrait d'App.jsx (Révision S1
// tranche 8). Déplacement pur : le texte est repris à l'identique depuis
// App.jsx (ReportIssuePanel, le panneau « Signaler un problème » exclusif à
// cet écran, voyage avec), seul l'export default a été ajouté.
// DOMAINE SENSIBLE (authentification) : ne rien « améliorer » ici — toute
// modification de logique passe par une revue dédiée.
// Tout l'état vient d'App.jsx via props (delegates, saveDelegate, admins,
// lists, config, onLogin, onClose).
// =============================================================================
import { REGION_FRANCFORT } from "./regionsFonctions";
import { useState, useEffect } from "react";
import { QRCodeSVG } from "./QrCodeParesseux";
import { normalizeEmail } from "./useFirebaseState";
import { analyserIdentite, miseEnFormeSaisie, nettoyerEspaces } from "./nomsPersonnes";
import { C, creerNotification, webAuthnSupported, enrolerPasskey } from "./appShared";
import { installerPrechargeDefi, prechargerDefi } from "./passkeyDefi";
import { methodeConnexion } from "./authGuard";
import { preparerEnrolement, oublierSecretEnAttente, formaterCle, estAppareilTactile } from "./totpEnrolement";
import PictoEnioAnime from "./PictoEnioAnime";
import { AuthBackdrop } from "./AuthBackdrop";
import { navTexte } from "./navConstants";
import { IconeTexte } from "./icones";

// Face ID, Touch ID ou Windows Hello sur CET appareil ? (07/10/2026) L'étape
// « Activez Face ID » ne se propose qu'à un appareil qui sait la faire :
// `pending.passkey` vaut undefined pour tout le monde depuis la migration v382
// et ne disait rien. Une vérification impossible ne bloque jamais la connexion.
async function biometrieDisponible() {
  if (!webAuthnSupported()) return false;
  try {
    const P = typeof window !== "undefined" ? window.PublicKeyCredential : null;
    if (!P || typeof P.isUserVerifyingPlatformAuthenticatorAvailable !== "function") return false;
    return (await P.isUserVerifyingPlatformAuthenticatorAvailable()) === true;
  } catch (_) { return false; }
}

// ─── Panneau "Signaler un problème" partagé par les étapes de connexion ──────
// Collecte les infos déjà saisies + contexte technique et envoie un email
// de diagnostic à pierre.marini@enio.fr via Cloud Function publique.
const ReportIssuePanel = ({ step, email, prenom, nom, mutuelle, errorMessage }) => {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  // Email de contact — toujours demandé explicitement pour garantir qu'on ait
  // un moyen de recontacter, même si l'utilisateur n'avait pas encore saisi
  // son email dans le formulaire principal (SOS depuis accueil sans login).
  const [contactEmail, setContactEmail] = useState(email || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState("");

  // Resynchronise l'email si le parent change la prop (ex: user tape son
  // email dans le champ login, puis clique SOS)
  useEffect(() => { if (email && !contactEmail) setContactEmail(email); }, [email, contactEmail]);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim());

  const send = async () => {
    if (!isEmailValid) {
      setResult("⚠️ Merci de renseigner votre adresse email pour qu'on puisse vous répondre.");
      return;
    }
    setSending(true); setResult("");
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = getFunctions(undefined, REGION_FRANCFORT);
      const signaler = httpsCallable(fn, "signalerProblemeConnexion");
      await signaler({
        step,
        email: contactEmail.trim(),     // email confirmé par l'utilisateur (source de vérité)
        emailOriginal: email,            // email saisi dans le formulaire (pour diag si différent)
        prenom, nom, mutuelle,
        errorMessage,
        userDescription: desc,
        userAgent: navigator.userAgent,
        url: window.location.href,
        appUrl: window.location.origin,
      });
      setResult("✅ Merci ! Nous avons bien reçu votre signalement et reviendrons vers vous au plus vite à l'adresse " + contactEmail.trim() + ".");
      setDesc("");
      setTimeout(() => { setOpen(false); setResult(""); }, 4500);
    } catch (e) {
      setResult("❌ L'envoi a échoué : " + (e.message || "erreur réseau") + ". Contactez pierre.marini@enio.fr directement.");
    }
    setSending(false);
  };

  if (!open) {
    return (
      <div style={{marginTop:18,paddingTop:14,borderTop:"1px solid var(--border-color)",textAlign:"center"}}>
        <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:6}}>
          Vous rencontrez des difficultés à vous connecter ?
        </div>
        <button type="button" onClick={() => setOpen(true)}
          style={{background:"none",border:"none",color:"#F49800",fontSize:"var(--fs-micro)",
            /* --cible-min : 0 au clavier-souris (lien inchange), 44 au doigt. */
            minHeight:"var(--cible-min)",
            fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>
          <IconeTexte nom="lifebuoy"/> Signalez-nous le problème
        </button>
      </div>
    );
  }

  return (
    <div style={{marginTop:14,padding:14,background:"#fff8f0",border:"1.5px solid #F49800",borderRadius:"var(--radius-md)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:"var(--fs-meta)",fontWeight:700,color:"#003F59"}}><IconeTexte nom="lifebuoy"/> Signaler un problème</div>
        <button type="button" onClick={() => { setOpen(false); setResult(""); }}
          style={{background:"none",border:"none",fontSize:"var(--fs-corps)",cursor:"pointer",color:"#888",lineHeight:1}}>×</button>
      </div>
      <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:10,lineHeight:1.5}}>
        Un email sera envoyé à l'équipe Aramis avec l'étape en cours et un diagnostic
        automatique de votre compte. <strong>Aucune donnée sensible</strong>
        (mot de passe, code 2FA) n'est transmise.
      </div>
      {/* Email de contact OBLIGATOIRE — permet la reprise de contact même si
          l'utilisateur n'avait pas (encore) tapé son email dans le form principal */}
      <div style={{marginBottom:8}}>
        <label style={{fontSize:"var(--fs-micro)",fontWeight:700,color:"#003F59",display:"block",marginBottom:4}}>
          Votre adresse email (obligatoire) *
        </label>
        <input type="email" value={contactEmail}
          onChange={e => setContactEmail(e.target.value.replace(/\s+/g, ""))}
          placeholder="prenom.nom@mutuelle.fr"
          disabled={sending}
          autoCapitalize="none" autoCorrect="off" spellCheck="false"
          style={{width:"100%",padding:"8px 10px",
            border:"1.5px solid "+(contactEmail && !isEmailValid ? "#E20816" : "var(--border-color)"),
            borderRadius:"var(--radius-sm)",fontSize:"var(--fs-micro)",fontFamily:"inherit",
            background:"var(--bg-card,#fff)",color:"var(--text-main)",boxSizing:"border-box"}}/>
        {contactEmail && !isEmailValid && (
          <div style={{fontSize:"var(--fs-micro)",color:"#E20816",marginTop:3}}>
            Format d'email invalide
          </div>
        )}
      </div>
      <textarea value={desc} onChange={e => setDesc(e.target.value)}
        placeholder="Décrivez ce qui bloque (optionnel), ex : « Je reçois l'email mais le QR code refuse mon code »"
        rows={3} disabled={sending}
        style={{width:"100%",padding:"8px 10px",border:"1px solid var(--border-color)",
          borderRadius:"var(--radius-sm)",fontSize:"var(--fs-micro)",fontFamily:"inherit",resize:"vertical",
          background:"var(--bg-card,#fff)",color:"var(--text-main)",boxSizing:"border-box"}}/>
      {result && (
        <div style={{marginTop:10,padding:"8px 10px",borderRadius:"var(--radius-sm)",fontSize:"var(--fs-micro)",
          background:result.startsWith("✅") ? "#e0f2f1" : "#fee",
          color:result.startsWith("✅") ? "var(--acc-navy)" : "var(--acc-red-ink)"}}>
          {result}
        </div>
      )}
      <div style={{display:"flex",gap:8,marginTop:10}}>
        <button type="button" onClick={send} disabled={sending || !isEmailValid}
          style={{flex:1,padding:"8px 14px",
            background: (sending || !isEmailValid) ? "#ccc" : "#F49800",
            color:"#fff",
            border:"none",borderRadius:"var(--radius-sm)",fontWeight:700,fontSize:"var(--fs-micro)",
            cursor:(sending || !isEmailValid) ? "not-allowed" : "pointer"}}>
          {sending ? "Envoi..." : <><IconeTexte nom="mailbox"/> Envoyer le signalement</>}
        </button>
      </div>
    </div>
  );
};

const AramisLogin = ({delegates, saveDelegate, admins=[], lists=[], config=null, onLogin, onClose}) => {
  // Deep-link : ?signup=1&email=...&prenom=...&nom=...&mutuelle=...
  const _params = new URLSearchParams(window.location.search);
  const _signup = _params.get("signup") === "1";
  const _preEmail = _params.get("email") || "";
  const _prePrenom = _params.get("prenom") || "";
  const _preNom = _params.get("nom") || "";
  // MSAL préchargé dès l'écran de connexion : le popup Microsoft s'ouvre alors
  // dans le geste du clic (Safari bloque les fenêtres ouvertes trop tard).
  useEffect(() => { import("./msal").then((m) => m.prechargerMsal()).catch(() => {}); }, []);
  // Défi WebAuthn préparé dès l'ouverture, pour les navigateurs dont la
  // dernière connexion s'est faite par passkey : au geste, Face ID / Touch ID
  // part sans attendre le réseau (iOS refuse une cérémonie tardive — 09/09).
  // Renouvelé tant que l'écran est visible, plafonné (limite par IP).
  useEffect(() => installerPrechargeDefi({ actif: webAuthnSupported() && methodeConnexion() === "passkey" }), []);
  const _preMut = _params.get("mutuelle") || "";
  const _preFonction = _params.get("fonction") || "";
  // Pré-remplir depuis la fiche delegate existante si email connu
  const _existDel = _preEmail ? delegates.find(d=>(d.email||"").toLowerCase()===_preEmail.toLowerCase()) : null;

  const [step,    setStep]    = useState(_signup ? "inscription" : "login");
  // Inscription — pré-rempli depuis URL + fiche delegate existante
  const [insPrenom,  setInsPrenom]  = useState(_prePrenom || _existDel?.prenom || "");
  const [insNom,     setInsNom]     = useState(_preNom || _existDel?.nom || "");
  const [insEmail,   setInsEmail]   = useState(_preEmail);
  const [insTel,     setInsTel]     = useState(_existDel?.telephone || "");
  const [insSansTel, setInsSansTel] = useState(_existDel?.sansTelephonePro || false);
  const [insMut,     setInsMut]     = useState(_preMut || _existDel?.mutuelle || "");
  const [insFonction,setInsFonction]= useState(_preFonction || _existDel?.fonction || "");
  const [insGenre,   setInsGenre]   = useState(_existDel?.genre || "NS");
  const [insDateNaiss,setInsDateNaiss]= useState(_existDel?.dateNaissance || "");
  const [insPwd,     setInsPwd]     = useState("");
  const [insPwd2,    setInsPwd2]    = useState("");
  const [insMsg,     setInsMsg]     = useState("");
  const [insSending, setInsSending] = useState(false);
  const [email,   setEmail]   = useState("");
  const [pwd,     setPwd]     = useState("");
  const [newPwd,  setNewPwd]  = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [totpCode,setTotpCode]= useState("");
  // Code à 6 chiffres qui VIENT d'être accepté (vérification ou activation de
  // la 2FA) : c'est la preuve forte que passkeyEnroler exige depuis le
  // 28/09/2026 quand la session a été ouverte par mot de passe. Gardé en
  // mémoire le temps de l'étape « passkey » seulement ; s'il a expiré entre-
  // temps (fenêtre ±60 s), on en redemande un sur place.
  const [codePreuve,setCodePreuve]= useState("");
  const [redemanderCode,setRedemanderCode]= useState(false);
  const [msg,     setMsg]     = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  // Second facteur biométrique : le bouton Touch ID / Face ID apparaît à
  // l'étape « totp » → on prépare le défi avant le geste, même logique.
  useEffect(() => { if (step === "totp" && pending?.passkey?.credentialId && webAuthnSupported()) prechargerDefi().catch(() => {}); }, [step, pending]);
  const [totpUri, setTotpUri] = useState("");
  const [totpSec, setTotpSec] = useState("");
  // Sur téléphone, le QR code de l'écran n'est pas scannable par le téléphone
  // lui-même : lien otpauth:// + clé à copier (incident du 24/09/2026).
  const [surTelephone] = useState(() => estAppareilTactile());
  const [cleCopiee, setCleCopiee] = useState(false);
  // ─── Honeypot anti-bot : champs invisibles pour les humains, remplis par
  // les scripts automatisés. Si non vides à la soumission, l'appel CF est
  // envoyé avec _hp:true → le serveur renvoie une fausse erreur d'identifiants.
  const [_hpLogin, _setHpLogin] = useState("");
  const [_hpSignup, _setHpSignup] = useState("");
  // showVerif supprimé — le popup de vérification contact est obsolète

  const handleLogin = async () => {
    setMsg(""); setLoading(true);
    const emailNorm = normalizeEmail(email);

    console.log("%c[LOGIN]","color:#F49800;font-weight:bold",`Server-side login pour "${emailNorm}"`);

    // Le formulaire est soumettable IMMÉDIATEMENT (perf boot) : la
    // vérification est 100 % server-side (aramisLoginCheck), le snapshot
    // delegates n'est plus un prérequis — il continue d'arriver en
    // arrière-plan et ne sert qu'à compléter la fiche après validation.
    // Appel Cloud Function aramisLoginCheck — vérification du mot de passe
    // server-side. Le client ne touche plus au passwordHash.
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = getFunctions(undefined, REGION_FRANCFORT);
      const check = httpsCallable(fn, "aramisLoginCheck");
      const res = await check({ email: emailNorm, password: pwd, _hp: _hpLogin || undefined });
      // Bascule d'identité vérifiée : dès que le serveur a validé le mot de passe,
      // il renvoie un Firebase custom token → on ouvre une session Firebase Auth
      // pour que les appels CF portent un request.auth.uid vérifié (ferme R3).
      // Non bloquant : en cas d'échec, la session Aramis reste fonctionnelle.
      if (res.data?.firebaseToken) {
        try {
          const [{ connecterJetonFirebase }, fb] = await Promise.all([
            import("./authGuard"), import("./firebase"),
          ]);
          await connecterJetonFirebase(fb.auth, res.data.firebaseToken, "mot-de-passe");
        } catch (e) { console.warn("[LOGIN] Firebase custom token:", e?.message); }
      }
      const profile = res.data?.profile;
      if (!profile) {
        setMsg("Réponse invalide du serveur — réessayez.");
        setLoading(false);
        return;
      }
      // Cherche le delegate complet pour les champs non inclus dans le profile
      // safe (et pour permettre la reprise du flow legacy de changement de mdp)
      const fullDelegate = delegates.find(x => x.id === profile.id) || profile;
      const d = { ...fullDelegate, ...profile };
      // La passkey vit dans credentials/ (non lisible client) : la CF renvoie
      // son id pour que le bouton biométrie de l'étape "totp" fonctionne même
      // si le snapshot delegates n'est pas encore arrivé (même mécanique que
      // le flux SSO Microsoft).
      if (profile.passkeyCredentialId && !d.passkey?.credentialId) {
        d.passkey = { credentialId: profile.passkeyCredentialId };
      }
      console.log(`%c[LOGIN] ✅ ${d.prenom} ${d.nom}`,"color:#1D948A;font-weight:bold");
      setPending(d); setLoading(false);
      if (d.firstLogin) { setStep("changePassword"); return; }
      if (!profile.hasTotp) {
        // Clé REPRISE si la PWA s'est rechargée pendant l'enrôlement (cf. totpEnrolement.js)
        preparerEnrolement(d.id, d.email).then(({secret,uri})=>{
          setTotpSec(secret); setTotpUri(uri); setStep("totpSetup");
        }).catch(()=>setMsg("Erreur chargement TOTP"));
        return;
      }
      setStep("totp");
    } catch (e) {
      setLoading(false);
      const code = e?.code || "";
      const errMsg = e?.message || "";
      console.warn("[LOGIN] erreur CF:", code, errMsg);
      if (code === "functions/permission-denied" || /Identifiants incorrects/.test(errMsg)) {
        setMsg("Email ou mot de passe incorrect.");
      } else if (code === "functions/failed-precondition" || /pas encore de mot de passe/.test(errMsg)) {
        setMsg("Ce compte existe mais n'a pas encore de mot de passe. Demandez un lien à l'administrateur.");
      } else if (code === "functions/resource-exhausted") {
        setMsg("Trop de tentatives. Attendez quelques minutes avant de réessayer.");
      } else if (code === "functions/unavailable" || /network|fetch/i.test(errMsg)) {
        if (!navigator.onLine) {
          setMsg("⚠ Pas de connexion Internet. Connectez-vous à Internet et réessayez.");
        } else {
          setMsg("Serveur temporairement indisponible. Réessayez dans un moment.");
        }
      } else {
        setMsg("Erreur de connexion. " + (errMsg ? "(" + errMsg + ")" : ""));
      }
    }
  };

  // ─── SSO Microsoft 365 (tous profils, e-mail professionnel) ────────────────
  // Le popup Microsoft fournit un jeton d'identité brut, vérifié SERVEUR par
  // aramisSsoMicrosoftLogin (signature, audience, tenant pro, e-mail ↔ compte
  // Aramis). Step-up MFA conditionnel : si le tenant Microsoft n'a pas exigé
  // de MFA (claim amr), on enchaîne sur le second facteur Aramis existant
  // (passkey/TOTP, ou enrôlement TOTP) — même posture que le flux mot de
  // passe. Si le tenant a fait la MFA (Authenticator, number matching…),
  // entrée directe.
  const handleMicrosoftLogin = async () => {
    setMsg(""); setLoading(true);
    let fbAuthMod = null, fbApp = null;
    try {
      const [authMod, fb] = await Promise.all([
        import("firebase/auth"), import("./firebase"),
      ]);
      fbAuthMod = authMod; fbApp = fb;
      // Deux chemins vers le MÊME jeton d'identité (audience vérifiée côté CF) :
      //   • MSAL (REACT_APP_SSO_MSAL=1, après configuration de la plateforme
      //     « application monopage » sur l'app Azure) : mémorise le compte, ce
      //     qui permet ensuite le renouvellement silencieux (authGuard/msal.js) ;
      //   • sinon le popup Firebase historique.
      let msIdToken = null;
      const { msalActif, connexionMicrosoftInteractive } = await import("./msal");
      if (msalActif()) {
        msIdToken = await connexionMicrosoftInteractive();
      } else {
        const provider = new authMod.OAuthProvider("microsoft.com");
        // `profile` : le jeton porte alors le nom d'utilisateur
        // (preferred_username), seule clé que le serveur admet ; le claim
        // `email` seul ne prouve rien (functions/lib/msToken.js, adresseSso).
        // MSAL demande la même portée (src/msal.js).
        provider.addScope("profile");
        // tenant "organizations" : comptes professionnels uniquement (pas de
        // comptes Microsoft personnels live/outlook — refusés aussi côté CF).
        provider.setCustomParameters({ tenant: "organizations", prompt: "select_account" });
        // Un compte du portail RH à la même adresse (mot de passe, créé par
        // le serveur à l'ouverture d'un espace salarié, functions/lib/porteRH.js)
        // fait refuser par Firebase la session Microsoft
        // (« account-exists-with-different-credential ») : le jeton Microsoft
        // est dans l'erreur, et seul le serveur décide (aramisSsoMicrosoftLogin).
        let cred = null;
        try {
          const result = await authMod.signInWithPopup(fb.auth, provider);
          cred = authMod.OAuthProvider.credentialFromResult(result);
        } catch (e) {
          if (e?.code !== "auth/account-exists-with-different-credential") throw e;
          cred = authMod.OAuthProvider.credentialFromError(e);
        }
        if (!cred?.idToken) throw new Error("Jeton Microsoft absent — réessayez.");
        msIdToken = cred.idToken;
      }

      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = getFunctions(undefined, REGION_FRANCFORT);
      const res = await httpsCallable(fn, "aramisSsoMicrosoftLogin")({ msIdToken });

      // Bascule d'identité vérifiée (même mécanique que le flux mot de passe) :
      // le custom token remplace la session popup Microsoft (uid = delegateId).
      if (res.data?.firebaseToken) {
        try { const { connecterJetonFirebase } = await import("./authGuard"); await connecterJetonFirebase(fb.auth, res.data.firebaseToken, "sso"); }
        catch (e) { console.warn("[SSO] Firebase custom token:", e?.message); }
      }
      const profile = res.data?.profile;
      if (!profile) {
        setMsg("Réponse invalide du serveur — réessayez.");
        setLoading(false); return;
      }
      const fullDelegate = delegates.find(x => x.id === profile.id) || profile;
      const d = { ...fullDelegate, ...profile };
      // La passkey vit dans credentials/ (non lisible client) : la CF renvoie
      // son id pour que le bouton biométrie de l'étape "totp" fonctionne.
      if (profile.passkeyCredentialId && !d.passkey?.credentialId) {
        d.passkey = { credentialId: profile.passkeyCredentialId };
      }
      console.log(`%c[SSO] ✅ ${d.prenom} ${d.nom} — MFA tenant: ${res.data?.mfaTenant ? "oui" : "non (step-up)"}`,"color:#1D948A;font-weight:bold");
      setPending(d); setLoading(false);
      if (res.data?.mfaTenant) { onLogin(d); return; } // le tenant a déjà exigé la MFA
      // Step-up : second facteur Aramis requis
      if (!profile.hasTotp && !d.passkey?.credentialId) {
        preparerEnrolement(d.id, d.email).then(({secret,uri})=>{
          setTotpSec(secret); setTotpUri(uri); setStep("totpSetup");
          setMsg("Votre organisation Microsoft n'a pas vérifié votre identité en deux étapes — configurez le second facteur Aramis pour continuer.");
        }).catch(()=>setMsg("Erreur chargement TOTP"));
        return;
      }
      setStep("totp");
      setMsg("Votre organisation Microsoft n'a pas vérifié votre identité en deux étapes — confirmez avec votre second facteur Aramis.");
    } catch (e) {
      setLoading(false);
      const code = e?.code || "";
      const errMsg = e?.message || "";
      // Nettoie la session popup Microsoft si la connexion n'a pas abouti
      try { if (fbAuthMod && fbApp) await fbAuthMod.signOut(fbApp.auth); } catch {}
      console.warn("[SSO] erreur:", code, errMsg);
      const msalCode = e?.errorCode || "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request" || code === "auth/user-cancelled"
          || msalCode === "user_cancelled") {
        setMsg(""); // fermeture volontaire du popup — pas une erreur
      } else if (code === "auth/popup-blocked" || msalCode === "popup_window_error") {
        setMsg("Votre navigateur a bloqué la fenêtre Microsoft. Autorisez les popups pour aramis.enio.fr et réessayez.");
      } else if (code === "functions/permission-denied" || code === "functions/failed-precondition") {
        setMsg(errMsg.replace(/^Firebase: /, "").replace(/ \(functions\/[a-z-]+\)\.?$/, ""));
      } else if (code === "functions/resource-exhausted") {
        setMsg("Trop de tentatives. Attendez quelques minutes avant de réessayer.");
      } else {
        setMsg("Connexion Microsoft impossible. " + (errMsg ? "(" + errMsg + ")" : "Réessayez ou utilisez la connexion classique."));
      }
    }
  };

  // ─── Connexion par passkey (sans e-mail ni mot de passe) ───────────────────
  // La cérémonie WebAuthn est vérifiée CÔTÉ SERVEUR (passkeyChallenge +
  // passkeyLogin) : défi émis par le serveur, signature/origine/rpId/biométrie
  // contrôlés avant l'émission du jeton. La passkey vaut deux facteurs
  // (appareil + biométrie) et résiste au phishing → elle ouvre directement la
  // session, sans second facteur supplémentaire.
  const handlePasskeyLogin = async () => {
    setMsg(""); setLoading(true);
    try {
      const { loginWithPasskey } = await import("./appShared");
      const data = await loginWithPasskey();
      if (!data?.profile) { setMsg("Réponse invalide du serveur — réessayez."); setLoading(false); return; }
      if (data.firebaseToken) {
        try {
          const fb = await import("./firebase");
          const { connecterJetonFirebase } = await import("./authGuard");
          await connecterJetonFirebase(fb.auth, data.firebaseToken, "passkey");
        } catch (e) { console.warn("[Passkey] Firebase custom token:", e?.message); }
      }
      const fullDelegate = delegates.find(x => x.id === data.profile.id) || data.profile;
      const d = { ...fullDelegate, ...data.profile };
      console.log(`%c[Passkey] ✅ ${d.prenom} ${d.nom}`, "color:#1D948A;font-weight:bold");
      setLoading(false);
      onLogin(d);
    } catch (e) {
      setLoading(false);
      const code = e?.code || "";
      const brut = e?.message || "";
      if (e?.name === "NotAllowedError") {
        setMsg(""); // annulation volontaire (Touch ID refusé / fermé)
      } else if (brut.includes("passkey_a_reenroler")) {
        setMsg("Cette passkey a été enregistrée dans un ancien format. Connectez-vous avec votre mot de passe, puis réactivez la biométrie depuis Mon profil.");
      } else if (code === "functions/permission-denied") {
        setMsg("Passkey non reconnue. Connectez-vous avec votre mot de passe, ou activez la biométrie depuis Mon profil.");
      } else if (code === "functions/resource-exhausted") {
        setMsg("Trop de tentatives. Attendez quelques minutes avant de réessayer.");
      } else {
        setMsg("Connexion par passkey impossible. Utilisez votre mot de passe.");
      }
    }
  };

  // ─── Mot de passe oublié : réinitialisation autonome ───────────────────────
  // Le serveur répond TOUJOURS la même chose (anti-énumération de comptes) :
  // impossible de savoir depuis cet écran si une adresse existe. Le lien reçu
  // par e-mail ne suffira pas : un second facteur sera exigé sur la page /reset.
  const handleMotDePasseOublie = async () => {
    const cible = (email || "").trim();
    if (!cible || !cible.includes("@")) {
      setMsg("Saisissez d'abord votre adresse e-mail, puis cliquez à nouveau.");
      return;
    }
    setLoading(true); setMsg("");
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = getFunctions(undefined, REGION_FRANCFORT);
      await httpsCallable(fn, "reinitDemander")({ email: cible, appUrl: window.location.origin });
      setMsg("Si un compte existe pour cette adresse, un e-mail vient d'être envoyé. Il vous sera demandé de confirmer avec votre biométrie ou votre code à 6 chiffres.");
    } catch (e) {
      if ((e?.code || "") === "functions/resource-exhausted") {
        setMsg("Trop de demandes. Réessayez dans une heure.");
      } else {
        setMsg("Demande impossible pour le moment. Réessayez plus tard.");
      }
    }
    setLoading(false);
  };

  const handleChangePwd = async () => {
    if(newPwd.length<8){setMsg("8 caractères minimum.");return;}
    if(newPwd!==newPwd2){setMsg("Les mots de passe ne correspondent pas.");return;}
    setLoading(true);
    // Le mot de passe est posé par le SERVEUR, dans credentials/ (fermée au
    // client). Avant le 16/09/2026, il était écrit sur la fiche délégué :
    // publié à qui sait lire Firestore, et sans effet sur la connexion pour
    // tout compte déjà migré — la personne repartait avec l'ancien.
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = getFunctions(undefined, REGION_FRANCFORT);
      await httpsCallable(fn, "aramisDefinirMotDePasse")({ ancien: pwd, nouveau: newPwd });
    } catch (e) {
      setLoading(false);
      const m = e?.message || "";
      setMsg(/incorrect/i.test(m) ? "Mot de passe actuel incorrect — reconnectez-vous."
        : /Reconnectez/i.test(m) ? "Session expirée — reconnectez-vous pour définir votre mot de passe."
        : "Enregistrement impossible : " + m);
      return;
    }
    const fiche = delegates.find(x => x.id === pending?.id) || {};
    const updated = {...fiche,...pending,firstLogin:false};
    setPending(updated); setLoading(false);
    if(!updated.totp?.secret && !pending?.hasTotp){
      preparerEnrolement(updated.id, updated.email).then(({secret,uri})=>{
        setTotpSec(secret); setTotpUri(uri); setStep("totpSetup");
      }).catch(()=>setMsg("Erreur chargement TOTP"));
      return;
    }
    setStep("totp");
  };

  const handleTotpSetup = async () => {
    if(totpCode.length!==6||!pending?.id) return;
    setLoading(true); setMsg("");
    try{
      // L'enrôlement passe par une Cloud Function : elle valide le code ET
      // persiste le secret dans credentials/{id} (source lue au login). Le
      // client ne peut pas écrire dans credentials → sans ça, l'enrôlement ne
      // « prend » jamais pour les comptes migrés (boucle de re-scan).
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = getFunctions(undefined, REGION_FRANCFORT);
      const setup = httpsCallable(fn, "aramisSetupTOTP");
      await setup({ delegateId: pending.id, secret: totpSec, totpCode });
      oublierSecretEnAttente(pending.id);
      const updated={...pending,totp:{verified:true}};
      setCodePreuve(totpCode); setRedemanderCode(false);
      setPending(updated); setLoading(false); setTotpCode("");
      // Proposer la biométrie si supportée et pas encore configurée
      if(await biometrieDisponible()){
        setStep("passkey"); setMsg("");
      } else {
        onLogin(updated);
      }
    }catch(e){
      setLoading(false);
      const code=e?.code||""; const errMsg=e?.message||"";
      if(code==="functions/permission-denied"||/TOTP incorrect/.test(errMsg)){
        setMsg("Code refusé : il ne correspond pas à la clé affichée ci-dessus. Dans votre application, supprimez les anciennes entrées « Aramis Enio », ajoutez celle-ci (bouton ou clé ci-dessus), puis saisissez le code qu'elle affiche. L'heure du téléphone doit être réglée automatiquement.");
      } else if(code==="functions/resource-exhausted"){
        setMsg("Trop de tentatives. Attendez quelques minutes.");
      } else {
        setMsg("Erreur d'activation 2FA : "+errMsg);
      }
    }
  };

  const handleTotpVerify = async () => {
    if (!totpCode || !pending?.id) return;
    setLoading(true); setMsg("");
    // Vérification TOTP server-side (le secret n'est plus lisible côté client
    // post-migration credentials — la Cloud Function fait le check).
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const fn = getFunctions(undefined, REGION_FRANCFORT);
      const verify = httpsCallable(fn, "aramisVerifyTOTP");
      await verify({ delegateId: pending.id, totpCode });
      setCodePreuve(totpCode); setRedemanderCode(false);
      setLoading(false);
      if (await biometrieDisponible()) {
        setStep("passkey"); setMsg("");
      } else {
        onLogin(pending);
      }
    } catch (e) {
      setLoading(false);
      const code = e?.code || "";
      const errMsg = e?.message || "";
      if (code === "functions/permission-denied" || /TOTP incorrect/.test(errMsg)) {
        // « expiré » orientait vers l'heure du téléphone ; le cas réel du 24/09
        // était une entrée « Aramis Enio » qui ne correspondait plus au compte.
        setMsg("Code refusé. Si votre application affiche plusieurs entrées « Aramis Enio », essayez celle ajoutée en dernier ; vérifiez aussi que l'heure du téléphone est réglée automatiquement. Si le code est toujours refusé, demandez à Enio de réinitialiser votre double authentification.");
      } else if (code === "functions/resource-exhausted") {
        setMsg("Trop de tentatives. Attendez quelques minutes.");
      } else {
        setMsg("Erreur vérification TOTP : " + errMsg);
      }
    }
  };

  const handlePasskeySetup = async () => {
    if (redemanderCode && codePreuve.length !== 6) { setMsg("Saisissez le code à 6 chiffres affiché maintenant par votre application."); return; }
    setLoading(true); setMsg("");
    try{
      // Défi serveur + attestation vérifiée : la clé est rangée dans
      // credentials/ par la CF, plus jamais sur la fiche publique. La preuve
      // forte est le code à 6 chiffres qui vient d'être accepté (28/09/2026) :
      // le jeton du mot de passe, seul, ne permet plus d'ajouter une passkey.
      // Elle part avec la demande de défi : si le serveur refuse, la
      // biométrie ne s'ouvre même pas (rien de créé, rien d'écrasé), et le
      // serveur fournit les clés déjà enregistrées à exclure.
      await enrolerPasskey(pending, { type: "appareil", totpCode: codePreuve || undefined });
      const updated = {...pending, passkeyActive:true};
      setPending(updated);
      onLogin(updated);
    }catch(e){
      const raison = e?.details?.raison;
      if (raison === "code_incorrect" || (raison === "preuve_requise" && e?.details?.codeAccepte)) {
        // Le code a expiré pendant l'étape (ou n'a jamais été saisi ici) : on
        // en redemande un sur place, sans renvoyer la personne au début.
        setCodePreuve(""); setRedemanderCode(true);
        setMsg("Votre code a expiré entre-temps. Saisissez le code affiché maintenant par votre application, puis activez à nouveau la biométrie.");
      } else if (e?.name === "InvalidStateError") {
        // Une passkey de ce compte vit déjà sur cet appareil (trousseau
        // iCloud) : exclue par le serveur, elle n'est pas écrasée.
        setMsg("Cet appareil porte déjà une passkey de votre compte : elle fonctionne déjà ici. Utilisez « Se connecter avec une passkey » la prochaine fois.");
      } else {
        // Refus serveur (code posé sans preuve forte, plafond atteint…) : son
        // message dit quoi faire. « Passer cette étape » reste disponible, la
        // connexion n'en dépend pas.
        setMsg("Erreur biométrie : "+e.message);
      }
      setLoading(false);
    }
  };

  return(
    <AuthBackdrop>
      <div className="auth-in" style={{background:"var(--bg-card,#fff)",borderRadius:"var(--radius-xl)",padding:"30px 32px 32px",
        width:"100%",maxWidth:420,border:"1px solid var(--border-color)",
        boxShadow:"0 28px 80px rgba(0,0,0,.45)"}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          {/* Picto Enio animé (vague de profondeur) — même magie que la page
              de garde, en format carte de connexion */}
          <PictoEnioAnime width={148} style={{margin:"-10px auto -6px"}}/>
          <div style={{fontWeight:700,fontSize:"var(--fs-chronique)",color:"var(--text-main)",
            marginTop:0,fontFamily:"Montserrat,sans-serif",letterSpacing:"calc(-.01em * var(--em-ajuste, 1))"}}>
            Aramis <span style={{color:"var(--tx-orange)",fontWeight:400,fontSize:"var(--fs-meta)"}}>· Connexion</span>
          </div>
        </div>

        {step==="login"&&<>
          {/* Plus de bannière « chargement de la base » : la vérification est
              server-side, le formulaire est utilisable dès l'affichage. */}
          <style>{`@keyframes spin {to{transform:rotate(360deg)}}`}</style>

          <div style={{marginBottom:12}}>
            <label className="lbl">Email</label>
            <input className="inp" type="email" value={email}
              onChange={e=>setEmail(e.target.value.replace(/\s+/g,""))}
              onKeyDown={e=>e.key==="Enter"&&handleLogin()}
              placeholder="prenom.nom@mutuelle.fr"
              /* autoComplete + enterKeyHint : sans "username"/"current-password",
                 le Trousseau iCloud ne propose RIEN et le mot de passe doit etre
                 tape a la main sur iPhone. enterKeyHint affiche "Aller" plutot
                 que "Retour" sur la touche de validation du clavier iOS. */
              autoComplete="username" inputMode="email" enterKeyHint="next"
              autoCapitalize="none" autoCorrect="off" spellCheck="false"/>
          </div>
          <div style={{marginBottom:16}}>
            <label className="lbl">Mot de passe</label>
            <input className="inp" type="password" value={pwd}
              onChange={e=>setPwd(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleLogin()}
              autoComplete="current-password" enterKeyHint="go"
              placeholder="Votre mot de passe"/>
          </div>
          {/* Honeypot anti-bot : invisible pour les humains, rempli par les scripts */}
          <div aria-hidden="true" style={{position:"absolute",left:"-9999px",top:"-9999px",
            width:1,height:1,overflow:"hidden",opacity:0,pointerEvents:"none"}}>
            <label>Site web (ne pas remplir)
              <input type="text" tabIndex={-1} autoComplete="off"
                name="website-url" value={_hpLogin}
                onChange={e=>_setHpLogin(e.target.value)}/>
            </label>
          </div>
          {msg&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-red)",marginBottom:12,fontWeight:600}}>{msg}</div>}
          <button className="btn btn-orange" style={{width:"100%"}}
            onClick={handleLogin} disabled={loading||!email||!pwd}>
            {loading ? "Connexion..." : "Se connecter"}
          </button>
          {/* SSO Microsoft 365 — comptes professionnels dont l'e-mail correspond
              à la fiche Aramis. Step-up MFA conditionnel géré par handleMicrosoftLogin. */}
          <div style={{display:"flex",alignItems:"center",gap:10,margin:"14px 0 10px"}}>
            <div style={{flex:1,height:1,background:"var(--border-color)"}}/>
            <span style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>ou</span>
            <div style={{flex:1,height:1,background:"var(--border-color)"}}/>
          </div>
          <button type="button" onClick={handleMicrosoftLogin} disabled={loading}
            style={{width:"100%",padding:"11px 16px",display:"flex",alignItems:"center",
              justifyContent:"center",gap:10,background:"var(--bg-card,#fff)",
              border:"1.5px solid var(--border-color)",borderRadius:"var(--radius-md)",fontSize:"var(--fs-meta)",
              minHeight:"var(--cible-min)",
              fontWeight:700,color:"var(--text-main)",cursor:loading?"not-allowed":"pointer",
              opacity:loading?.6:1,fontFamily:"inherit"}}>
            <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="0" y="0" width="10" height="10" fill="#F25022"/>
              <rect x="11" y="0" width="10" height="10" fill="#7FBA00"/>
              <rect x="0" y="11" width="10" height="10" fill="#00A4EF"/>
              <rect x="11" y="11" width="10" height="10" fill="#FFB900"/>
            </svg>
            {loading ? "Connexion..." : "Se connecter avec Microsoft"}
          </button>
          <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",textAlign:"center",marginTop:6,lineHeight:1.5}}>
            Compte professionnel Microsoft 365 uniquement : l'adresse doit correspondre à votre compte Aramis.
          </div>
          {/* Connexion par passkey : ni e-mail ni mot de passe. L'appareil
              propose les comptes qu'il connaît, la biométrie fait foi, et la
              cérémonie WebAuthn est vérifiée côté serveur. Affiché seulement
              si le navigateur sait faire. */}
          {webAuthnSupported() && (
            <>
              <button type="button" onClick={handlePasskeyLogin} disabled={loading}
                style={{width:"100%",marginTop:10,padding:"11px 16px",display:"flex",alignItems:"center",
                  justifyContent:"center",gap:10,background:"var(--bg-card,#fff)",
                  border:"1.5px solid var(--border-color)",borderRadius:"var(--radius-md)",fontSize:"var(--fs-meta)",
                  fontWeight:700,color:"var(--text-main)",cursor:loading?"not-allowed":"pointer",
                  opacity:loading?.6:1,fontFamily:"inherit"}}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 2a5 5 0 0 1 5 5c0 2.05-1.23 3.81-3 4.58V13l-1.5 1.5L14 16l-1.5 1.5L14 19l-2 2-1.5-1.5V11.58C8.73 10.81 7.5 9.05 7.5 7A5 5 0 0 1 12 2Z"
                    stroke="var(--tx-teal,#1D948A)" strokeWidth="1.8" strokeLinejoin="round"/>
                  <circle cx="12" cy="6.5" r="1.4" fill="var(--tx-teal,#1D948A)"/>
                </svg>
                {loading ? "Connexion..." : "Se connecter avec une passkey"}
              </button>
              <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",textAlign:"center",marginTop:6,lineHeight:1.5}}>
                Touch ID, Face ID ou code de l'appareil : aucun mot de passe à saisir.
              </div>
            </>
          )}
          <button className="btn btn-ghost btn-sm" style={{width:"100%",marginTop:10}}
            onClick={onClose}>Annuler</button>
          {/* Ces deux liens mesuraient 14 px de haut : sur l'ecran que TOUS les
              membres voient, ils etaient hors d'atteinte du pouce. Le plancher
              de 44 pt passe par --cible-min (0 au clavier-souris, 44 au doigt) :
              au bureau, les liens gardent leur taille et leur espacement. */}
          <div style={{textAlign:"center",marginTop:14,display:"flex",flexDirection:"column",gap:8}}>
            <button style={{background:"none",border:"none",color:"var(--tx-teal)",fontSize:"var(--fs-micro)",
              minHeight:"var(--cible-min)",cursor:"pointer",textDecoration:"underline"}}
              onClick={()=>{setStep("inscription");setMsg("");}}>
              Créer mon compte
            </button>
            <button style={{background:"none",border:"none",color:"var(--text-sub)",fontSize:"var(--fs-micro)",
              minHeight:"var(--cible-min)",cursor:"pointer",textDecoration:"underline"}}
              onClick={handleMotDePasseOublie} disabled={loading}>
              Mot de passe oublié ?
            </button>
          </div>
          <ReportIssuePanel step="login" email={email} errorMessage={msg}/>
        </>}

        {step==="inscription"&&<>
          <div style={{fontSize:"var(--fs-meta)",fontWeight:700,color:"var(--text-main)",marginBottom:4}}>
            Créer mon compte Aramis
          </div>
          <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:14}}>
            Votre demande sera examinée par un administrateur avant activation.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label className="lbl">Prénom *</label>
              <input className="inp" value={insPrenom} onChange={e=>setInsPrenom(e.target.value)} placeholder="Prénom"/>
            </div>
            <div>
              <label className="lbl">Nom *</label>
              <input className="inp" value={insNom} onChange={e=>setInsNom(e.target.value)} placeholder="NOM"/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label className="lbl">Email *</label>
            <input className="inp" type="email" value={insEmail}
              onChange={e=>setInsEmail(e.target.value.replace(/\s+/g,""))}
              placeholder="prenom.nom@mutuelle.fr" autoCapitalize="none" autoCorrect="off" spellCheck="false"/>
          </div>
          <div style={{marginBottom:10}}>
            <label className="lbl">Téléphone professionnel {!insSansTel && "*"}</label>
            <input className="inp" type="tel" value={insSansTel?"":insTel}
              onChange={e=>setInsTel(e.target.value)}
              disabled={insSansTel}
              placeholder="06 XX XX XX XX"
              style={{opacity:insSansTel?.5:1}}/>
            <label style={{display:"flex",alignItems:"center",gap:6,marginTop:6,fontSize:"var(--fs-micro)",color:"var(--text-sub)",cursor:"pointer"}}>
              <input type="checkbox" checked={insSansTel}
                onChange={e=>{setInsSansTel(e.target.checked); if(e.target.checked)setInsTel("");}}/>
              Je n'ai pas de numéro de téléphone professionnel
            </label>
          </div>
          <div style={{marginBottom:10}}>
            <label className="lbl">Mutuelle / Organisation *</label>
            <select className="inp" value={insMut} onChange={e=>setInsMut(e.target.value)}>
              <option value="">Sélectionner…</option>
              <option value="Enio">Enio (équipe)</option>
              {/* Liste SOURCE OFFICIELLE uniquement : memberLists + config.mutuelles fallback.
                  ⚠️ On NE fusionne PAS avec les noms tapés dans les fiches delegates : sinon
                  une faute de frappe historique (ex: "SMH Hauts de France" au lieu de "SMH")
                  pollue la liste signup et provoque la création d'une mutuelle fantôme. */}
              {(() => {
                const allNames = new Set();
                (lists||[]).forEach(l => { if (l.name && !l.name.toLowerCase().includes("enio")) allNames.add(l.name); });
                (config?.mutuelles||[]).forEach(m => { if (m && !m.toLowerCase().includes("enio")) allNames.add(m); });
                // Cas particulier : on conserve la mutuelle exacte du delegate existant
                // SEULEMENT si on est dans un flux de reset password / lien magique (_existDel
                // pré-rempli par lookup email), pour ne pas casser un compte légacy.
                if (_existDel?.mutuelle && !_existDel.mutuelle.toLowerCase().includes("enio")) {
                  allNames.add(_existDel.mutuelle);
                }
                return [...allNames].sort((a,b)=>a.localeCompare(b,"fr")).map(name => (
                  <option key={name} value={name}>{name}</option>
                ));
              })()}
            </select>
            {/* Avertissement si l'utilisateur a une mutuelle pré-remplie qui n'est pas dans la liste */}
            {insMut && insMut!=="Enio" && !insMut.toLowerCase().includes("enio") &&
              ![...(lists||[]).map(l=>l.name), ...(config?.mutuelles||[]), ...(delegates||[]).map(d=>d.mutuelle), _existDel?.mutuelle]
                .filter(Boolean).includes(insMut) && (
              <div style={{fontSize:"var(--fs-micro)",color:"#F49800",marginTop:4,fontWeight:600}}>
                <IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> "{insMut}" n'est pas dans la liste officielle. Contactez l'admin si besoin.
              </div>
            )}
          </div>
          <div style={{marginBottom:10}}>
            <label className="lbl">Fonction *</label>
            <input className="inp" value={insFonction} onChange={e=>setInsFonction(e.target.value)} placeholder="Ex : Directeur général, Présidente..."/>
          </div>
          <div className="grid2" style={{marginBottom:10}}>
            <div>
              <label className="lbl">Genre *</label>
              <select className="inp" value={insGenre} onChange={e=>setInsGenre(e.target.value)}
                style={{WebkitAppearance:"menulist",appearance:"menulist"}}>
                <option value="NS">Sélectionner…</option>
                <option value="F">Femme</option>
                <option value="H">Homme</option>
              </select>
            </div>
            <div>
              <label className="lbl">Date de naissance *</label>
              <input className="inp" type="date" value={insDateNaiss} onChange={e=>setInsDateNaiss(e.target.value)}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label className="lbl">Mot de passe *</label>
            <input className="inp" type="password" value={insPwd} onChange={e=>setInsPwd(e.target.value)} autoComplete="new-password" placeholder="Créez votre mot de passe"/>
            {/* Indicateur des règles en temps réel */}
            <div style={{marginTop:6,padding:"8px 10px",background:"var(--card-inner)",borderRadius:"var(--radius-sm)",fontSize:"var(--fs-micro)",lineHeight:1.5}}>
              <div style={{fontWeight:700,color:"var(--text-sub)",marginBottom:3}}>Règles :</div>
              <div style={{color:insPwd.length>=8?navTexte(C.teal):"var(--text-sub)"}}>
                {insPwd.length>=8?"✓":"○"} Au moins 8 caractères {insPwd.length>0&&`(${insPwd.length})`}
              </div>
              <div style={{color:/[A-Z]/.test(insPwd)?navTexte(C.teal):"var(--text-sub)"}}>
                {/[A-Z]/.test(insPwd)?"✓":"○"} Au moins 1 majuscule (recommandé)
              </div>
              <div style={{color:/[0-9]/.test(insPwd)?navTexte(C.teal):"var(--text-sub)"}}>
                {/[0-9]/.test(insPwd)?"✓":"○"} Au moins 1 chiffre (recommandé)
              </div>
              <div style={{color:"var(--text-sub)",marginTop:4,fontStyle:"italic"}}>
                <IconeTexte nom="bulb"/> Évitez les copier-coller depuis un email (caractères invisibles possibles). Tapez directement.
              </div>
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <label className="lbl">Confirmer le mot de passe *</label>
            <input className="inp" type="password" value={insPwd2} onChange={e=>setInsPwd2(e.target.value)} autoComplete="new-password" placeholder="Répéter le mot de passe"/>
            {insPwd2 && insPwd!==insPwd2 && (
              <div style={{marginTop:4,fontSize:"var(--fs-micro)",color:"var(--tx-red)",fontWeight:600}}>
                <IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> Les mots de passe ne correspondent pas
              </div>
            )}
            {insPwd2 && insPwd===insPwd2 && insPwd.length>=8 && (
              <div style={{marginTop:4,fontSize:"var(--fs-micro)",color:"var(--tx-teal)",fontWeight:600}}>
                ✓ Les mots de passe correspondent
              </div>
            )}
          </div>
          {/* Honeypot anti-bot pour l'inscription */}
          <div aria-hidden="true" style={{position:"absolute",left:"-9999px",top:"-9999px",
            width:1,height:1,overflow:"hidden",opacity:0,pointerEvents:"none"}}>
            <label>Site web (ne pas remplir)
              <input type="text" tabIndex={-1} autoComplete="off"
                name="website-url" value={_hpSignup}
                onChange={e=>_setHpSignup(e.target.value)}/>
            </label>
          </div>
          {insMsg&&<div style={{fontSize:"var(--fs-micro)",marginBottom:10,fontWeight:600,
            color:navTexte(insMsg.startsWith("✅")?C.teal:C.red)}}>{insMsg}</div>}
          <button className="btn btn-orange" style={{width:"100%"}}
            disabled={insSending
              ||insPrenom.trim().length<2||insNom.trim().length<2
              ||!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(insEmail.trim())
              ||(!insSansTel&&!insTel.trim())
              ||!insMut||insMut.trim().length<2
              ||!insGenre||insGenre==="NS"||!insDateNaiss
              ||insPwd.length<8||insPwd!==insPwd2}
            onClick={async()=>{
              setInsSending(true); setInsMsg("");
              try{
                // Double validation côté front (les rules Firestore valident aussi côté serveur)
                const _p = insPrenom.trim(), _n = insNom.trim(), _m = (insMut||"").trim();
                if (_p.length < 2 || _n.length < 2 || _m.length < 2) {
                  setInsMsg("Veuillez renseigner prénom, nom et mutuelle (min. 2 caractères).");
                  setInsSending(false); return;
                }
                if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(insEmail.trim())) {
                  setInsMsg("Adresse email invalide.");
                  setInsSending(false); return;
                }
                const emailNorm = normalizeEmail(insEmail);
                // Vérifier si un compte existe déjà avec cet email
                const existing = delegates.find(d=>(d.email||"").toLowerCase()===emailNorm && d.passwordHash);
                if(existing){
                  setInsMsg("Un compte existe déjà avec cet email. Connectez-vous directement.");
                  setInsSending(false); return;
                }
                // Vérifier si une demande est déjà en cours
                // Le mot de passe choisi part au SERVEUR, qui le hache et le
                // range hors de vue (demandesCompteSecrets/, fermée au client) :
                // jusqu'au 16/09/2026 son empreinte était écrite dans
                // demandesCompte/, collection lisible par n'importe qui. Le
                // rattachement à une fiche existante est décidé côté serveur.
                // « Prénom NOM » (30/09/2026, src/nomsPersonnes.js) : chaque
                // champ est mis en forme s'il est sûr, sinon il part tel que
                // saisi (espaces nettoyées) : « de Gaulle », « McDonald »,
                // « Maria do Céu » ne sont jamais passés en capitales ici, où
                // la personne écrit son propre nom. L'inversion n'est pas
                // cherchée (champs étiquetés, tapés par l'intéressé) : le
                // prénom est analysé face au NOM tel qu'il partira.
                const _analyseIns = analyserIdentite({ prenom:insPrenom, nom:insNom });
                const _nomIns = _analyseIns.alertes.some(a=>a.champ==="nom") ? nettoyerEspaces(insNom) : _analyseIns.apres.nom;
                const _prenomIns = miseEnFormeSaisie({ prenom:insPrenom, nom:_nomIns }, null).prenom ?? nettoyerEspaces(insPrenom);
                const {getFunctions:_gf,httpsCallable:_hc} = await import("firebase/functions");
                const _fn = _gf(undefined,REGION_FRANCFORT);
                const _res = await _hc(_fn,"demandeCompteDeposer")({
                  prenom:_prenomIns, nom:_nomIns,
                  email:emailNorm,
                  telephone:insSansTel?"":insTel.trim(),
                  sansTelephonePro:!!insSansTel,
                  mutuelle:insMut,
                  fonction:insFonction.trim(), genre:insGenre, dateNaissance:insDateNaiss,
                  motDePasse:insPwd,
                  _hp: _hpSignup || undefined,
                });
                if(_res?.data?.deja){
                  setInsMsg("Une demande est déjà en cours pour cet email. Patientez la validation d'un administrateur.");
                  setInsSending(false); return;
                }
                // Journal des administrateurs (la demande se traite dans sa file) :
                // plus d'adresse e-mail dans une collection lisible par tous.
                await creerNotification("demande_compte",{
                  version:2,
                  titre:`Demande d'accès à Aramis : ${_prenomIns} ${_nomIns}`,
                  message:insMut ? `Mutuelle : ${insMut}.` : "",
                  lien:"admin",
                  membreNom:_prenomIns+" "+_nomIns,
                  auteur:_prenomIns+" "+_nomIns,
                });
                // Notifier les admins
                const {getFunctions,httpsCallable} = await import("firebase/functions");
                const fn = getFunctions(undefined,REGION_FRANCFORT);
                await httpsCallable(fn,"notifierDemandeCompte")({
                  prenom:_prenomIns, nom:_nomIns,
                  email:insEmail.trim(), mutuelle:insMut,
                  appUrl:window.location.origin,
                  _hp: _hpSignup || undefined,
                }).catch(()=>{}); // non bloquant
                setInsMsg("✅ Demande envoyée ! Un administrateur examinera votre dossier et vous recevrez un email de confirmation.");
                setInsSending(false);
              }catch(e){
                setInsMsg("Erreur : "+(e.message||"inconnue"));
                setInsSending(false);
              }
            }}>
            {insSending?"Envoi en cours...":<><IconeTexte nom="mail-down"/> Envoyer ma demande</>}
          </button>
          <button className="btn btn-ghost btn-sm" style={{width:"100%",marginTop:10}}
            onClick={()=>{setStep("login");setInsMsg("");}}>
            ← Retour à la connexion
          </button>
          <ReportIssuePanel step="inscription"
            email={insEmail} prenom={insPrenom} nom={insNom} mutuelle={insMut}
            errorMessage={insMsg}/>
        </>}

        {step==="changePassword"&&<>
          <div style={{background:"#FEF3C7",borderRadius:"var(--radius-md)",padding:"10px 14px",
            marginBottom:16,fontSize:"var(--fs-micro)",color:"#92400E"}}>
            Première connexion : choisissez un nouveau mot de passe.
          </div>
          <div style={{marginBottom:10}}>
            <label className="lbl">Nouveau mot de passe</label>
            <input className="inp" type="password" value={newPwd}
              onChange={e=>setNewPwd(e.target.value)} autoFocus
              placeholder="Créez votre mot de passe"/>
            {/* Indicateur des règles en temps réel (comme au signup) */}
            <div style={{marginTop:6,padding:"8px 10px",background:"var(--card-inner)",borderRadius:"var(--radius-sm)",fontSize:"var(--fs-micro)",lineHeight:1.5}}>
              <div style={{fontWeight:700,color:"var(--text-sub)",marginBottom:3}}>Règles :</div>
              <div style={{color:newPwd.length>=8?navTexte(C.teal):"var(--text-sub)"}}>
                {newPwd.length>=8?"✓":"○"} Au moins 8 caractères {newPwd.length>0&&`(${newPwd.length})`}
              </div>
              <div style={{color:/[A-Z]/.test(newPwd)?navTexte(C.teal):"var(--text-sub)"}}>
                {/[A-Z]/.test(newPwd)?"✓":"○"} Au moins 1 majuscule (recommandé)
              </div>
              <div style={{color:/[0-9]/.test(newPwd)?navTexte(C.teal):"var(--text-sub)"}}>
                {/[0-9]/.test(newPwd)?"✓":"○"} Au moins 1 chiffre (recommandé)
              </div>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label className="lbl">Confirmer</label>
            <input className="inp" type="password" value={newPwd2}
              onChange={e=>setNewPwd2(e.target.value)}
              placeholder="Répéter le mot de passe"/>
            {newPwd2 && newPwd!==newPwd2 && (
              <div style={{marginTop:4,fontSize:"var(--fs-micro)",color:"var(--tx-red)",fontWeight:600}}>
                <IconeTexte nom="alert-triangle" couleur="var(--tx-orange)"/> Les mots de passe ne correspondent pas
              </div>
            )}
            {newPwd2 && newPwd===newPwd2 && newPwd.length>=8 && (
              <div style={{marginTop:4,fontSize:"var(--fs-micro)",color:"var(--tx-teal)",fontWeight:600}}>
                ✓ Les mots de passe correspondent
              </div>
            )}
          </div>
          {msg&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-red)",marginBottom:12,fontWeight:600}}>{msg}</div>}
          {/* Hint visible quand le bouton est grisé */}
          {!loading && (newPwd.length<8 || newPwd!==newPwd2) && (newPwd.length>0 || newPwd2.length>0) && (
            <div style={{fontSize:"var(--fs-micro)",color:"var(--tx-orange)",marginBottom:8,fontWeight:600,textAlign:"center",background:"rgba(244, 152, 0,.08)",padding:"6px 10px",borderRadius:"var(--radius-sm)"}}>
              {newPwd.length<8 ? <><IconeTexte nom="lock"/>{` Ajoutez ${8-newPwd.length} caractère(s) pour continuer`}</> : <><IconeTexte nom="lock"/> Les deux mots de passe doivent être identiques</>}
            </div>
          )}
          <button className="btn btn-orange" style={{width:"100%"}}
            onClick={handleChangePwd}
            disabled={loading||newPwd.length<8||newPwd!==newPwd2}>
            {loading?"Enregistrement...":"Continuer"}
          </button>
          <ReportIssuePanel step="changePassword"
            email={email || pending?.email}
            prenom={pending?.prenom} nom={pending?.nom}
            errorMessage={msg}/>
        </>}

        {step==="totpSetup"&&<>
          <div style={{fontSize:"var(--fs-meta)",fontWeight:600,color:"var(--text-main)",marginBottom:12}}>
            Configuration de la double authentification
          </div>

          {/* WARNING EXPLICITE : l'appareil photo ne marche PAS */}
          <div style={{background:"#fef2f2",border:"2px solid #E20816",borderRadius:"var(--radius-md)",
            padding:"14px 16px",marginBottom:14}}>
            <div style={{fontSize:"var(--fs-meta)",fontWeight:700,color:"#E20816",marginBottom:8,
              display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:"var(--fs-accroche)"}}><IconeTexte nom="alert-triangle" couleur="var(--acc-orange-ink)" titre="Attention"/></span>
              <span>N'utilisez PAS l'appareil photo de votre téléphone !</span>
            </div>
            <div style={{fontSize:"var(--fs-micro)",color:"#333",lineHeight:1.6}}>
              Il vous faut une <strong>application d'authentification dédiée</strong> : pas l'app photo, pas un scanner de QR générique.
            </div>
          </div>

          {/* Instructions étape par étape */}
          <div style={{background:"#f0f7ff",border:"1px solid #2AA9E1",borderRadius:"var(--radius-md)",
            padding:"14px 16px",marginBottom:16}}>
            <div style={{fontSize:"var(--fs-micro)",fontWeight:700,color:"var(--acc-blue-ink)",marginBottom:8}}>
              <IconeTexte nom="device-mobile"/> Marche à suivre (2 minutes)
            </div>
            <ol style={{margin:"0",paddingLeft:20,fontSize:"var(--fs-micro)",color:"#333",lineHeight:1.8}}>
              <li>
                Installez l'une de ces applications sur votre téléphone (gratuites, depuis l'App Store ou Google Play) :
                <div style={{marginTop:6,display:"flex",gap:10,flexWrap:"wrap"}}>
                  <span style={{padding:"3px 8px",background:"#fff",borderRadius:"var(--radius-sm)",
                    border:"1px solid #ccc",fontSize:"var(--fs-micro)",fontWeight:600}}>
                    <IconeTexte nom="pastille" couleur="var(--acc-blue-ink)"/> Microsoft Authenticator
                  </span>
                  <span style={{padding:"3px 8px",background:"#fff",borderRadius:"var(--radius-sm)",
                    border:"1px solid #ccc",fontSize:"var(--fs-micro)",fontWeight:600}}>
                    <IconeTexte nom="pastille" couleur="var(--acc-green-ink)"/> Google Authenticator
                  </span>
                </div>
              </li>
              <li style={{marginTop:6}}>
                <strong>Ouvrez l'application</strong> que vous venez d'installer.
              </li>
              {surTelephone ? (
                <li style={{marginTop:6}}>
                  Revenez ici et touchez <strong>« Ajouter à mon application d'authentification »</strong>.
                  Si rien ne s'ouvre, touchez <strong>« Copier la clé »</strong> puis, dans l'app : <strong>« + »</strong> →
                  <strong> « Saisir une clé de configuration »</strong> (type « Basé sur l'heure ») et collez-la.
                </li>
              ) : (<>
                <li style={{marginTop:6}}>
                  Dans l'app, cherchez le bouton <strong>« + »</strong> ou <strong>« Ajouter un compte »</strong>, puis choisissez <strong>« Scanner un QR code »</strong>.
                </li>
                <li style={{marginTop:6}}>
                  Pointez votre téléphone vers le QR code ci-dessous.
                </li>
              </>)}
              <li style={{marginTop:6}}>
                L'app affiche un <strong>code à 6 chiffres</strong> qui change toutes les 30 secondes. Saisissez-le ici.
              </li>
            </ol>
          </div>

          {totpUri&&surTelephone&&(
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
              <a className="btn btn-teal" href={totpUri}
                style={{width:"100%",textAlign:"center",textDecoration:"none"}}>
                <IconeTexte nom="device-mobile"/> Ajouter à mon application d'authentification
              </a>
              <button type="button" className="btn btn-ghost" style={{width:"100%"}}
                onClick={async()=>{
                  try { await navigator.clipboard.writeText(totpSec); setCleCopiee(true); }
                  catch { setCleCopiee(false); }
                }}>
                {cleCopiee ? "✓ Clé copiée · collez-la dans l'application" : <><IconeTexte nom="clipboard-list"/> Copier la clé</>}
              </button>
              <div style={{fontSize:"var(--fs-micro)",color:"var(--text-main)",wordBreak:"break-all",
                background:"#f3f4f6",borderRadius:"var(--radius-sm)",padding:"8px 10px",textAlign:"center",
                fontFamily:"ui-monospace,Menlo,monospace",letterSpacing:1}}>
                {formaterCle(totpSec)}
              </div>
              <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",lineHeight:1.5}}>
                Si la page se recharge quand vous revenez de l'application, c'est la même clé :
                ne l'ajoutez pas une seconde fois, saisissez simplement le code.
              </div>
            </div>
          )}
          {totpUri&&(
            <div style={{textAlign:"center",marginBottom:16}}>
              {surTelephone&&(
                <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:6}}>
                  Ou, depuis un autre appareil, scannez ce QR code :
                </div>
              )}
              <div style={{display:"inline-block",padding:12,background:"#fff",
                borderRadius:"var(--radius-md)",border:"1px solid var(--border-color)",
                boxShadow:"0 2px 8px rgba(0,0,0,.08)"}}>
                <QRCodeSVG value={totpUri} size={surTelephone?140:180}/>
              </div>
              {!surTelephone&&(
                <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginTop:10,
                  wordBreak:"break-all",background:"#f3f4f6",
                  borderRadius:"var(--radius-sm)",padding:"8px 10px"}}>
                  Clé manuelle (si scan impossible) : <strong>{formaterCle(totpSec)}</strong>
                </div>
              )}
            </div>
          )}
          <div style={{marginBottom:16}}>
            <label className="lbl">Code à 6 chiffres affiché dans votre app</label>
            <input className="inp" value={totpCode} maxLength={6}
              inputMode="numeric" autoComplete="one-time-code"
              onChange={e=>setTotpCode(e.target.value.replace(/[^0-9]/g,""))}
              onKeyDown={e=>e.key==="Enter"&&handleTotpSetup()}
              placeholder="000000"
              style={{letterSpacing:8,fontSize:"var(--fs-chronique)",textAlign:"center"}}/>
          </div>
          {msg&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-red)",marginBottom:12,fontWeight:600}}>{msg}</div>}
          <button className="btn btn-orange" style={{width:"100%"}}
            onClick={handleTotpSetup} disabled={totpCode.length!==6}>
            Activer et accéder
          </button>
          <ReportIssuePanel step="totpSetup" email={email} errorMessage={msg}/>
        </>}

        {step==="passkey"&&<>
          <div style={{textAlign:"center",marginBottom:16}}>
            <div style={{fontSize:"var(--fs-display)",marginBottom:12}}><IconeTexte nom="lock" titre="Verrouillé"/></div>
            <div style={{fontSize:"var(--fs-corps)",fontWeight:700,color:"var(--text-main)",fontFamily:"Montserrat,sans-serif",marginBottom:6}}>
              Activez Face ID ou Touch ID
            </div>
            <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",lineHeight:1.6,marginBottom:20}}>
              La prochaine fois, vous entrerez d'un regard ou d'un doigt, sans mot de passe ni code. Un seul geste suffit maintenant.
            </div>
          </div>
          {msg&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-red)",marginBottom:12,fontWeight:600}}>{msg}</div>}
          {redemanderCode&&(
            <div style={{marginBottom:12}}>
              <label className="lbl" htmlFor="passkey-code-preuve">Code à 6 chiffres</label>
              <input id="passkey-code-preuve" className="inp" value={codePreuve} maxLength={6}
                inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                onChange={e=>setCodePreuve(e.target.value.replace(/[^0-9]/g,""))}
                onKeyDown={e=>e.key==="Enter"&&handlePasskeySetup()}
                style={{letterSpacing:8,textAlign:"center"}}/>
            </div>
          )}
          <button className="btn btn-orange" style={{width:"100%",fontSize:"var(--fs-nav)",padding:"14px 20px",
            display:"flex",alignItems:"center",justifyContent:"center",gap:10}}
            onClick={handlePasskeySetup} disabled={loading||(redemanderCode&&codePreuve.length!==6)}>
            {loading?"Activation…":<><IconeTexte nom="id"/> Activer Face ID ou Touch ID</>}
          </button>
          <button className="btn btn-ghost" style={{width:"100%",marginTop:10,fontSize:"var(--fs-micro)"}}
            onClick={()=>onLogin(pending)}>
            Passer cette étape →
          </button>
          <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",textAlign:"center",marginTop:12}}>
            Vous pourrez toujours l'activer plus tard dans votre profil.
          </div>
          <ReportIssuePanel step="passkey"
            email={email || pending?.email}
            prenom={pending?.prenom} nom={pending?.nom}
            errorMessage={msg}/>
        </>}

        {step==="totp"&&<>
          <div style={{fontSize:"var(--fs-meta)",fontWeight:600,color:"var(--text-main)",marginBottom:12}}>
            Double authentification
          </div>
          {/* WebAuthn si passkey disponible */}
          {pending?.passkey?.credentialId&&webAuthnSupported()&&(
            <button className="btn btn-orange" style={{width:"100%",marginBottom:12,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:"var(--fs-nav)"}}
              onClick={async()=>{
                // Second facteur biométrique VÉRIFIÉ CÔTÉ SERVEUR (2026-07-27).
                // Avant : authenticatePasskey() se contentait du succès de la
                // cérémonie dans le navigateur — contourner le JS suffisait à
                // sauter le second facteur. On passe désormais par
                // passkeyChallenge/passkeyLogin, et on exige que la passkey
                // présentée soit bien celle du compte en cours d'authentification.
                setMsg(""); setLoading(true);
                try{
                  const { loginWithPasskey } = await import("./appShared");
                  const data = await loginWithPasskey();
                  if(data?.profile?.id && data.profile.id === pending.id){
                    if(data.firebaseToken){
                      try{
                        const fb = await import("./firebase");
                        const { connecterJetonFirebase } = await import("./authGuard");
                        await connecterJetonFirebase(fb.auth, data.firebaseToken, "passkey-step-up");
                      }catch(err){ console.warn("[Passkey step-up] custom token:", err?.message); }
                    }
                    onLogin(pending);
                  }else if(data?.profile?.id){
                    setMsg("Cette passkey appartient à un autre compte.");
                  }else{setMsg("Authentification échouée.");}
                }catch(e){
                  if(e?.name==="NotAllowedError") setMsg("");
                  else if((e?.message||"").includes("passkey_a_reenroler")) setMsg("Passkey enregistrée dans un ancien format — saisissez votre code à 6 chiffres, puis réactivez la biométrie depuis Mon profil.");
                  else setMsg("Vérification biométrique refusée — saisissez votre code à 6 chiffres.");
                }
                setLoading(false);
              }} disabled={loading}>
              {loading?"Vérification...":<><IconeTexte nom="id"/> Touch ID / Face ID</>}
            </button>
          )}
          <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",textAlign:"center",marginBottom:12}}>
            {pending?.passkey?.credentialId&&webAuthnSupported()?"Ou saisissez votre code":"Saisissez le code affiché dans votre application d'authentification."}
          </div>
          <input className="inp" value={totpCode} maxLength={6}
            inputMode="numeric" autoComplete="one-time-code"
            onChange={e=>setTotpCode(e.target.value.replace(/[^0-9]/g,""))}
            onKeyDown={e=>e.key==="Enter"&&handleTotpVerify()}
            placeholder="000000"
            style={{letterSpacing:8,fontSize:"var(--fs-chronique)",textAlign:"center",marginBottom:16}}/>
          {msg&&<div style={{fontSize:"var(--fs-micro)",color:"var(--tx-red)",marginBottom:12,fontWeight:600}}>{msg}</div>}
          <button className="btn btn-orange" style={{width:"100%"}}
            onClick={handleTotpVerify} disabled={totpCode.length!==6||loading}>Valider le code</button>
          <button className="btn btn-ghost btn-sm" style={{width:"100%",marginTop:10}}
            onClick={()=>{setStep("login");setTotpCode("");setMsg("");}}>Retour</button>
          <ReportIssuePanel step="totp"
            email={email || pending?.email}
            prenom={pending?.prenom} nom={pending?.nom}
            errorMessage={msg}/>
        </>}
      </div>
    </AuthBackdrop>
  );
};

export default AramisLogin;
